import React, { useState, useEffect, useCallback } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Save,
  Loader2,
  Plus,
  Trash2,
  AlertCircle,
  Search,
  Package,
  BookOpen,
  Paperclip,
} from 'lucide-react';
import bomService from '../services/bomService';
import productService from '../services/productService';
import processTemplateService from '../services/processTemplateService';
import FileAttachmentManager from './FileAttachmentManager';

const BomForm = ({ initialData, finishedProduct, onSuccess, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [finishedProductId, setFinishedProductId] = useState('');
  const [finishedProductCode, setFinishedProductCode] = useState('');
  const [finishedProductName, setFinishedProductName] = useState('');
  const [finishedQuantity, setFinishedQuantity] = useState(1);
  const [processTemplateId, setProcessTemplateId] = useState('');
  const [processTemplates, setProcessTemplates] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [status, setStatus] = useState('active');
  const [isDefault, setIsDefault] = useState(false);
  const [notes, setNotes] = useState('');
  const [processNotes, setProcessNotes] = useState('');  // 工艺备注/知识库链接
  const [attachments, setAttachments] = useState([]);  // 附件列表

  // Product search
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchType, setProductSearchType] = useState('finished'); // 'finished' or 'material'
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [currentMaterialIndex, setCurrentMaterialIndex] = useState(-1);

  // Fetch process templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templates = await processTemplateService.getTemplates();
        setProcessTemplates(templates || []);
      } catch (err) {
        console.error('Failed to fetch process templates:', err);
      }
    };
    fetchTemplates();
  }, []);

  // Initialize form data
  useEffect(() => {
    if (initialData) {
      setFinishedProductId(initialData.finished_product_id);
      setFinishedProductCode(initialData.finished_product_code);
      setFinishedProductName(initialData.finished_product_name);
      setFinishedQuantity(initialData.finished_quantity || 1);
      // Convert empty/null to 'none' for Radix UI Select compatibility
      setProcessTemplateId(initialData.process_template_id || '');
      setMaterials(
        initialData.materials?.map((m, index) => ({
          ...m,
          _tempId: m.line_item_id || `temp-${index}`,
        })) || []
      );
      setStatus(initialData.status || 'draft');
      setIsDefault(initialData.is_default || false);
      setNotes(initialData.notes || '');
      setProcessNotes(initialData.process_notes || '');  // 加载工艺备注
      setAttachments(initialData.attachments || []);  // 加载附件列表
    } else if (finishedProduct) {
      // Pre-fill finished product from prop
      setFinishedProductId(finishedProduct._id || finishedProduct.product_id); // Handle both formats if needed, assuming _id usually
      // If finishedProduct passed is from analysis (which has product_id as code, _id as database id usually missing in analysis sometimes, let's check)
      // Actually UnifiedConversionDialog passes product from analysis which has product_id, product_code, product_name
      
      // Let's robustly handle the input object
      if (finishedProduct._id) {
        setFinishedProductId(finishedProduct._id);
        setFinishedProductCode(finishedProduct.product_id || finishedProduct.product_code); // product_id is usually code in product collection, but product_code in others
        setFinishedProductName(finishedProduct.name || finishedProduct.product_name);
      } else if (finishedProduct.product_id) {
         // Maybe it's the product ID string directly? No, usually object.
         // In UnifiedConversionDialog: product_id (db ID), product_code, product_name
         setFinishedProductId(finishedProduct.product_id);
         setFinishedProductCode(finishedProduct.product_code);
         setFinishedProductName(finishedProduct.product_name);
      }
    }
  }, [initialData, finishedProduct]);

  // Search products
  const searchProducts = useCallback(async () => {
    if (!productSearchTerm.trim()) {
      setProductSearchResults([]);
      return;
    }

    try {
      setProductSearchLoading(true);
      // Determine product type filter based on current search context
      const typeFilter = productSearchType === 'finished' ? 'finished' : 'raw_material';
      
      const response = await productService.searchProducts(
        productSearchTerm,
        1,
        20,
        typeFilter
      );
      setProductSearchResults(response.products || []);
    } catch (err) {
      console.error('Failed to search products:', err);
    } finally {
      setProductSearchLoading(false);
    }
  }, [productSearchTerm, productSearchType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchOpen && productSearchTerm) {
        searchProducts();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm, productSearchOpen, searchProducts]);

  const openProductSearch = (type, materialIndex = -1) => {
    setProductSearchType(type);
    setCurrentMaterialIndex(materialIndex);
    setProductSearchTerm('');
    setProductSearchResults([]);
    setProductSearchOpen(true);
  };

  const selectProduct = (product) => {
    if (productSearchType === 'finished') {
      setFinishedProductId(product._id);
      setFinishedProductCode(product.product_id);
      setFinishedProductName(product.name);
    } else if (currentMaterialIndex >= 0) {
      const updated = [...materials];
      updated[currentMaterialIndex] = {
        ...updated[currentMaterialIndex],
        material_product_id: product._id,
        material_product_code: product.product_id,
        material_name: product.name,
        unit: product.unit || 'pcs',
      };
      setMaterials(updated);
    }
    setProductSearchOpen(false);
  };

  const addMaterial = () => {
    setMaterials([
      ...materials,
      {
        _tempId: `temp-${Date.now()}`,
        material_product_id: '',
        material_product_code: '',
        material_name: '',
        quantity_per: 1,
        unit: 'pcs',
        scrap_rate: 0,
      },
    ]);
  };

  const updateMaterial = (index, field, value) => {
    const updated = [...materials];
    updated[index] = { ...updated[index], [field]: value };
    setMaterials(updated);
  };

  const removeMaterial = (index) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validate
    if (!finishedProductId) {
      setError('请选择成品。');
      return;
    }

    if (!processTemplateId) {
      setError('请选择工序模板。');
      return;
    }

    if (materials.length === 0) {
      setError('请至少添加一种物料。');
      return;
    }

    const invalidMaterials = materials.filter((m) => !m.material_product_id || !m.quantity_per);
    if (invalidMaterials.length > 0) {
      setError('请完善所有物料信息。');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const bomData = {
        finished_product_id: finishedProductId,
        finished_quantity: finishedQuantity,
        process_template_id: processTemplateId,
        materials: materials.map((m) => ({
          line_item_id: m.line_item_id || m._tempId,
          material_product_id: m.material_product_id,
          material_product_code: m.material_product_code,
          material_name: m.material_name,
          quantity_per: Number(m.quantity_per),
          unit: m.unit,
          scrap_rate: Number(m.scrap_rate) || 0,
        })),
        status,
        is_default: isDefault,
        notes,
        process_notes: processNotes,  // 保存工艺备注
        attachments: attachments,  // 保存附件列表
      };

      let result;
      if (initialData?._id) {
        result = await bomService.updateBom(initialData._id, bomData);
      } else {
        result = await bomService.createBom(bomData);
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error('Failed to save BOM:', err);
      setError(`Failed to save BOM: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="space-y-6 pb-20">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Finished Product */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            成品
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>产品</Label>
              <div className="flex gap-2">
                <Input
                  value={finishedProductCode ? `${finishedProductCode} - ${finishedProductName}` : ''}
                  placeholder="选择产品..."
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={() => openProductSearch('finished')}
                  disabled={!!finishedProduct} // Disable search if pre-filled fixed product
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>产出数量</Label>
              <Input
                type="number"
                value={finishedQuantity}
                onChange={(e) => setFinishedQuantity(Number(e.target.value))}
                min={1}
              />
              <p className="text-sm text-muted-foreground">
                每次生产的产出数量
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label>状态</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">草稿</SelectItem>
                  <SelectItem value="active">有效</SelectItem>
                  <SelectItem value="obsolete">已作废</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="process-template">
                工序模板 (Process Template) <span className="text-destructive">*</span>
              </Label>
              <Select
                value={processTemplateId}
                onValueChange={setProcessTemplateId}
              >
                <SelectTrigger id="process-template">
                  <SelectValue placeholder="请选择工序模板" />
                </SelectTrigger>
                <SelectContent>
                  {processTemplates.map(template => (
                    <SelectItem key={template._id} value={template._id}>
                      {template.name} ({template.steps?.length || 0} 道工序)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 flex items-end">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isDefault"
                  checked={isDefault}
                  onCheckedChange={setIsDefault}
                />
                <Label htmlFor="isDefault">设为此产品的默认BOM</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Materials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              物料 ({materials.length})
            </span>
            <Button variant="outline" size="sm" onClick={addMaterial}>
              <Plus className="h-4 w-4 mr-2" />
              添加物料
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {materials.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              未添加物料。点击"添加物料"开始。
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料</TableHead>
                    <TableHead className="w-24">用量</TableHead>
                    <TableHead className="w-16">单位</TableHead>
                    <TableHead className="w-24">损耗%</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materials.map((material, index) => (
                    <TableRow key={material._tempId || material.line_item_id}>
                      <TableCell>
                        <div className="flex gap-2 min-w-[200px]">
                          <Input
                            value={
                              material.material_product_code
                                ? `${material.material_product_code} - ${material.material_name}`
                                : ''
                            }
                            placeholder="选择物料..."
                            readOnly
                            className="flex-1"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openProductSearch('material', index)}
                          >
                            <Search className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={material.quantity_per}
                          onChange={(e) =>
                            updateMaterial(index, 'quantity_per', Number(e.target.value))
                          }
                          min={0}
                          step="0.001"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={material.unit}
                          onChange={(e) => updateMaterial(index, 'unit', e.target.value)}
                          placeholder="pcs"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={(material.scrap_rate || 0) * 100}
                          onChange={(e) =>
                            updateMaterial(index, 'scrap_rate', Number(e.target.value) / 100)
                          }
                          min={0}
                          max={100}
                          step="0.1"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMaterial(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Process Notes - 工艺备注/知识库链接 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            工艺备注 / 知识库链接
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={processNotes}
            onChange={(e) => setProcessNotes(e.target.value)}
            placeholder="输入工艺文档链接或工艺说明，例如: http://wiki.company.com/process/abc123..."
            rows={3}
          />
          <p className="text-sm text-muted-foreground mt-2">
            支持输入外部知识库URL或工艺说明文本，方便查阅详细工艺流程
          </p>
        </CardContent>
      </Card>

      {/* Attachments - 附件管理 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            工艺文档附件
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!initialData?._id ? (
            <div className="p-6 text-center bg-muted/50 rounded-lg border border-dashed border-muted-foreground/30">
              <Paperclip className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-sm font-medium mb-1">
                附件功能将在BOM保存后可用
              </p>
              <p className="text-sm text-muted-foreground">
                请先保存BOM基本信息，然后即可上传相关工艺文档
              </p>
            </div>
          ) : (
            <FileAttachmentManager
              entityType="bom"
              entityId={initialData._id}
              title="工艺文档与附件"
              maxFiles={20}
              maxSizeMB={100}
              allowedTypes={[
                '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
                '.jpg', '.jpeg', '.png', '.gif', '.bmp',
                '.dwg', '.dxf', '.step', '.stp', '.iges', '.igs',
                '.zip', '.rar', '.7z'
              ]}
              compact={false}
            />
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>备注</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="可选BOM备注..."
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex justify-end gap-4 pt-4 border-t sticky bottom-0 bg-background z-10 p-4 -mx-4 -mb-6">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            取消
          </Button>
        )}
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          保存BOM
        </Button>
      </div>

      {/* Product Search Dialog */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-2xl z-[100]">
          <DialogHeader>
            <DialogTitle>
              {productSearchType === 'finished' ? '选择成品' : '选择物料'}
            </DialogTitle>
            <DialogDescription>
              从产品目录中搜索并选择产品。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="按产品编号或名称搜索..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {productSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : productSearchResults.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {productSearchTerm ? '未找到产品。' : '输入搜索词查找产品。'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>产品编号</TableHead>
                      <TableHead>名称</TableHead>
                      <TableHead>单位</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productSearchResults.map((product) => (
                      <TableRow
                        key={product._id}
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => selectProduct(product)}
                      >
                        <TableCell className="font-mono">{product.product_id}</TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{product.unit || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductSearchOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BomForm;
